/**
 * Feature Flags: Common methods.
 */

import { AxiosError } from '../../../internal';
import { Dict, ErrorType, LinkType } from '../../types';

/** @internal */
export const FEATURE_FLAGS_LOGGER_COMPONENT = 'featureFlags';

// Used for validating app ids and feature ids.
/** @internal */
export enum IdType {
  App = 'app',
  Feature = 'feature',
  User = 'user',
  Group = 'group',
}

// *** General-purpose Methods ***

// Create an error object with a given name.
/** @internal */
export function createNamedError(name: string, message: string): Error {
  const err = new Error(message);
  err.name = name;
  return err;
}

// Create a formatter function for sending messages to a logger.
/** @internal */
export function createLogMsgFormatter(
  submodule: string
): (msg: string | AxiosError<ErrorType> | unknown) => string {
  return (msg: string | AxiosError<ErrorType> | unknown) => {
    const svcMsg = (msg as AxiosError<ErrorType>).response?.data?.message;
    return `(${submodule}) ${svcMsg ? `ServiceError: ${svcMsg}` : msg}`;
  };
}

// *** Common REST Methods ***

// Finds a HATEOAS link object with the matching relation; otherwise returns undefined.
/** @internal */
export function findLink(links: LinkType[] | undefined, rel: string): LinkType | undefined {
  return links ? links.find((link) => link.rel === rel) : undefined;
}

// Converts an array of HATEOAS link objects into a map by link relation.
/** @internal */
export function createLinkMap(links: LinkType[] | undefined): Dict<LinkType> {
  return links
    ? links.reduce((map: Dict<LinkType>, link) => {
        if (link.rel) {
          map[link.rel] = link;
        }
        return map;
      }, {})
    : {};
}

// *** Helper Methods Specific to Feature Flags ***

// Throw error if any id is invalid.
/** @internal */
export function validateIds(ids: string[], idType: IdType): void {
  // will throw error if any id is invalid
  ids.forEach((id) => validateId(id, idType));
}

// Throw error if id is invalid.
/** @internal */
export function validateId(id: string, idType: string): void {
  // commas are invalid characters due to CSV-style filters in URI query parameters
  if (typeof id !== 'string' || id === '' || id !== id.trim() || id.includes(',')) {
    throw createNamedError('NameError', `invalid ${idType} id: '${id}'`);
  }
}
